import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  TablePagination,
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Stack,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  HowToReg as HowToRegIcon,
  AssignmentTurnedIn as OnboardIcon,
} from '@mui/icons-material';
import { useGetCandidateScheduledList } from '../../hooks/useCandidate';
import { markCandidateJoined, markCandidateDropped } from '../../api';
import SendOnboardingDialog from './SendOnboardingDialog';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, AVATAR_SX } from '../common/tableStyles';
import { formatDate } from '../../utils/dateFormat';

// ---- FORTHEYE soft-card tokens (shared with the employee portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

// Soft tinted status pill.
const chipSx = (fg, bg) => ({ fontWeight: 700, bgcolor: bg, color: fg, border: 'none' });

const SelectedCandidate = () => {
  const { data: sheduledlist } = useGetCandidateScheduledList();
  const [onbApplicant, setOnbApplicant] = useState(null);   // open the send-onboarding dialog for
  const [onbSent, setOnbSent] = useState(() => new Set());
  const [joined, setJoined] = useState(() => new Set());
  const [dropped, setDropped] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);
  const [snack, setSnack] = useState(null);                 // { severity, msg }
  const [joinTarget, setJoinTarget] = useState(null);       // applicant being joined
  const [joinEmpNo, setJoinEmpNo] = useState('');
  const [joinBusy, setJoinBusy] = useState(false);
  const [joinErr, setJoinErr] = useState(null);

  // Open the onboarding dialog (edit salary/DOJ, preview letters, then send).
  const handleSendOnboarding = (applicant) => {
    if (!applicant?.id) { setSnack({ severity: 'error', msg: 'Missing candidate id.' }); return; }
    if (!applicant.email_address) { setSnack({ severity: 'error', msg: 'This candidate has no email address on file.' }); return; }
    setOnbApplicant(applicant);
  };

  // Joined -> open a dialog where HR assigns the real employee number, then
  // activate the prospective employee (they get portal access + welcome email).
  const openJoin = (applicant) => {
    setJoinTarget(applicant);
    setJoinEmpNo('');
    setJoinErr(null);
  };

  const confirmJoin = async () => {
    if (!joinTarget) return;
    setJoinBusy(true); setJoinErr(null);
    try {
      const res = await markCandidateJoined(joinTarget.id, joinEmpNo.trim() || undefined);
      setJoined((p) => new Set(p).add(joinTarget.id));
      setDropped((p) => { const n = new Set(p); n.delete(joinTarget.id); return n; });
      const welcomeMsg = res?.welcome_email_sent ? ' A welcome email has been sent.' : '';
      const no = res?.employee_no && !String(res.employee_no).startsWith('ONB-') ? ` (${res.employee_no})` : '';
      setSnack({ severity: 'success', msg: `${joinTarget.name} activated${no} — they can now log in to the portal.${welcomeMsg}` });
      setJoinTarget(null);
    } catch (e) {
      setJoinErr(e?.response?.data?.error || 'Could not mark as joined.');
    } finally { setJoinBusy(false); }
  };

  // Dropped -> resign the prospective employee (kept for audit); candidate rejected.
  const handleDropped = async (applicant) => {
    if (!window.confirm(`Mark ${applicant.name} as dropped (did not join)?`)) return;
    setBusyId(applicant.id);
    try {
      await markCandidateDropped(applicant.id);
      setDropped((p) => new Set(p).add(applicant.id));
      setJoined((p) => { const n = new Set(p); n.delete(applicant.id); return n; });
      setSnack({ severity: 'success', msg: `${applicant.name} marked as dropped.` });
    } catch (e) {
      setSnack({ severity: 'error', msg: e?.response?.data?.error || 'Could not mark as dropped.' });
    } finally { setBusyId(null); }
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const filteredCandidates = Array.isArray(sheduledlist)
    ? sheduledlist
      .filter((applicant) => applicant.statuses.some((status) => status.status === 'selected'))
      .filter((applicant) =>
        applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.email_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        applicant.phone_number?.includes(searchTerm) ||
        applicant.role?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const paginatedCandidates = filteredCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <HowToRegIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Selected Candidates
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Invite, onboard and track the candidates you have selected.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Search card */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: { xs: 1.75, sm: 2 }, mb: { xs: 2, md: 3 }, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search candidates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 320 }, '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8fafc' } }}
        />
      </Paper>

      {/* Table card */}
      <Paper elevation={0} sx={CARD_SX}>
        <TableContainer component={Paper} elevation={0} sx={{ ...TABLE_PAPER_SX, boxShadow: 'none', border: 'none' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>S.No</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Candidate</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Role</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Interview Date</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>Resume</TableCell>
                <TableCell sx={{ ...HEAD_CELL_SX, textAlign: 'center' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCandidates.length > 0 ? paginatedCandidates.map((applicant, index) => (
                <TableRow key={index} hover sx={TABLE_ROW_SX}>
                  <TableCell sx={BODY_CELL_SX}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={AVATAR_SX}>
                        {applicant.name?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{applicant.name || 'N/A'}</Typography>
                        <Typography variant="caption" color="text.secondary">{applicant.email_address}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={BODY_CELL_SX}>{applicant.role || 'N/A'}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>{applicant.interview_date ? formatDate(applicant.interview_date) : 'N/A'}</TableCell>
                  <TableCell sx={BODY_CELL_SX}>
                    {applicant.final_status === 'joined' ? (
                      <Chip label="Joined" size="small" sx={chipSx('#16a34a', '#f0fdf4')} />
                    ) : applicant.final_status === 'pending' ? (
                      <Chip label="Onboarding" size="small" sx={chipSx('#f59e0b', '#fffbeb')} />
                    ) : applicant.final_status === 'rejected' ? (
                      <Chip label="Dropped" size="small" sx={chipSx('#ef4444', '#fef2f2')} />
                    ) : (
                      <Chip label="Selected" size="small" sx={chipSx(ACCENT, '#eef4ff')} />
                    )}
                  </TableCell>
                  <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                    {applicant.resume ? (
                      <IconButton
                        component="a"
                        href={applicant.resume}
                        target="_blank"
                        sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, '&:hover': { bgcolor: `${ACCENT}22` } }}
                      >
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ ...BODY_CELL_SX, textAlign: 'center' }}>
                    <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant={onbSent.has(applicant.id) ? 'outlined' : 'contained'}
                        color="secondary"
                        disableElevation
                        startIcon={<OnboardIcon />}
                        onClick={() => handleSendOnboarding(applicant)}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                          ...(onbSent.has(applicant.id)
                            ? { color: '#0e7c86', borderColor: '#bfe3e5' }
                            : { background: 'linear-gradient(135deg,#12a3ad,#0e7c86)', '&:hover': { background: 'linear-gradient(135deg,#0e7c86,#0a5a62)' } }) }}
                      >
                        {onbSent.has(applicant.id) ? 'Onboarding sent' : 'Send onboarding'}
                      </Button>
                      <Button
                        size="small"
                        color="success"
                        variant={(joined.has(applicant.id) || applicant.final_status === 'joined') ? 'contained' : 'outlined'}
                        disableElevation
                        disabled={busyId === applicant.id}
                        onClick={() => openJoin(applicant)}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                      >
                        Joined
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant={(dropped.has(applicant.id) || applicant.final_status === 'rejected') ? 'contained' : 'outlined'}
                        disableElevation
                        disabled={busyId === applicant.id}
                        onClick={() => handleDropped(applicant)}
                        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
                      >
                        Dropped
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8, border: 'none' }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <HowToRegIcon />
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No selected candidates found.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCandidates.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 2 }}
        />
      </Paper>

      <SendOnboardingDialog
        applicant={onbApplicant}
        onClose={() => setOnbApplicant(null)}
        onSent={(a, res) => {
          setOnbSent((prev) => new Set(prev).add(a.id));
          setSnack({ severity: 'success', msg: res?.result?.email_sent === false ? 'Onboarding provisioned (email pending — check mail config).' : `Onboarding pack sent to ${a.name}.` });
          setOnbApplicant(null);
        }}
      />

      <Dialog open={!!joinTarget} onClose={joinBusy ? undefined : () => setJoinTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17, pb: 0.5 }}>
          Mark as Joined{joinTarget ? ` — ${joinTarget.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#64757d', mb: 2 }}>
            Assign this employee&apos;s official number. This activates their portal login and sends a welcome email. Leave blank to keep the temporary ID for now.
          </Typography>
          <TextField
            autoFocus
            size="small"
            fullWidth
            label="Employee number"
            placeholder="e.g. 4857"
            value={joinEmpNo}
            onChange={(e) => setJoinEmpNo(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !joinBusy) confirmJoin(); }}
          />
          {joinErr && <Typography sx={{ color: '#c0392b', fontSize: 13, mt: 1.5 }}>{joinErr}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setJoinTarget(null)} disabled={joinBusy} sx={{ textTransform: 'none', color: '#64757d' }}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmJoin}
            disabled={joinBusy}
            startIcon={joinBusy ? <CircularProgress size={16} color="inherit" /> : <HowToRegIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5 }}
          >
            Confirm join
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(null)} sx={{ borderRadius: '12px' }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SelectedCandidate;
